import {useEffect, useState} from 'react';
import AurDrawer from '../../common/aur-drawer/AurDrawer';
import AurDrawerPanelContainer from '../AurDrawerPanelContainer';
import styles from '../../../styles/components/clientdetails/projectDrawerStyle.module.scss';
import {Box} from '@mui/material';
import AurDrawerNavigation from '../../common/aur-drawer-navigation/AurDrawerNavigation';
import AurButton from '../../common/aur-button/AurButton';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  updateSelectedProjectId,
  updateSelectedProjectIndex,
} from '../../../redux/slices/clientDetailsSlice';
import {useModal} from '../../../contexts/modalContext/ModalContext';
import {
  IProjectDetails,
  ProjectFields,
} from '../../../types/project-type/projectDetailsType';
import {
  getFilteredProjectList,
  getProjectByProjectId,
  updateProjectByProjectId,
} from '../../../api/ClientManagementService';
import WorkspacesSection from './WorkspacesSection';
import ProjectClientInformation from './ProjectClientInformation';
import BusinessStructure from './BusinessStructure';
import ProjectSectionDivider from './ProjectSectionDivider';
import ProjectSectionHeader from './SectionHeader';
import {t} from 'i18next';
import ProjectInfoSection from './ProjectInfoSection';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import ProjectOverseeingAttorneys from './ProjectOverseeingAttorneys';
import ProjectDescription from './ProjectDescription';

export type PorjectSectionBaseProp = {
  isEdit?: boolean;
  onEditClickHandler: (editFlag: Partial<EditFlag>) => void;
  onCancelClickHandler: (editFlag: Partial<EditFlag>) => void;
  onSaveChangeHandler: (projectFields: ProjectFields) => Promise<boolean>;
};
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {TriggerProjectsAndSummary} from '../../../redux/slices/clientListSlice';
import {toODataQuery} from '../../common/aur-data-grid-pro/useServerSideDataGrid';
import AurAskLioDrawerHeaderButton from '../../aur-ask-lio/AurAskLioDrawerHeaderButton';
import ECADatabaseSection from './ECADatabaseSection';
import usePermissionsByProjectId from '../../../hooks/permissions/usePermissionsByProjectId';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';

export type EditFlag = {
  businessStructure: boolean;
  projectDescription: boolean;
  clientInformation: boolean;
  overseeingAttorneys: boolean;
};
const defaultEditFlag: EditFlag = {
  businessStructure: false,
  projectDescription: false,
  clientInformation: false,
  overseeingAttorneys: false,
};

const ProjectDrawer = () => {
  const dispatch = useDispatch();
  const [projectData, setProjectData] = useState<IProjectDetails>();
  const [showLoader, setShowLoader] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [departmentDrawerNavigationState, setDepartmentDrawerNavigationState] =
    useState({currentItemOrder: 0, totalItemCount: 0});
  const [projectEditFlag, setProjectEditFlag] =
    useState<EditFlag>(defaultEditFlag);
  const {showModal} = useModal();

  const clientId = useSelector(
    (state: RootState) => state.clientList.selectedClientId
  );
  const selectedProjectId = useSelector(
    (state: RootState) => state.department.selectedProjectId
  );
  const selectedProjectIndex = useSelector(
    (state: RootState) => state.department.selectedProjectIndex
  );
  const projectTableFilterState = useSelector(
    (state: RootState) => state.department.projectTableFilterState
  );

  const {showToast} = useToast();
  const {getPermissionsByClientIdProjectId} = usePermissionsByProjectId();
  const {hasEditProjectProfilePerm} = usePermissionsByPage(
    PAGE_NAME.EDIT_PROJECT_PROFILE,
    selectedProjectId?.toLowerCase()
  );

  const isEditInProgress = () => {
    if (
      projectEditFlag.businessStructure ||
      projectEditFlag.clientInformation ||
      projectEditFlag.overseeingAttorneys ||
      projectEditFlag.projectDescription
    ) {
      return true;
    } else {
      return false;
    }
  };

  const departmentDrawerNavigatonHandler = (
    itemOrder: number,
    isPrevious?: boolean
  ) => {
    if (isEditInProgress()) {
      showModal({
        title: t(
          'clientDetailsPage.projectDrawer.modal.navigatePanelUnsavedChangePopup.title'
        ),
        description: t(
          'clientDetailsPage.projectDrawer.modal.navigatePanelUnsavedChangePopup.description'
        ),
        labelOkButton: t(
          'clientDetailsPage.projectDrawer.modal.navigatePanelUnsavedChangePopup.okButtonLabel'
        ),
        labelCancelButton: t(
          'clientDetailsPage.projectDrawer.modal.navigatePanelUnsavedChangePopup.cancelButtonLabel'
        ),
        onOk: () => {
          setProjectEditFlag(defaultEditFlag);
          const nextIndex = resolveNextIndex(
            selectedProjectIndex ?? 0,
            itemOrder,
            isPrevious
          );
          dispatch(updateSelectedProjectIndex(nextIndex));
        },
      });
    } else {
      const nextIndex = resolveNextIndex(
        selectedProjectIndex ?? 0,
        itemOrder,
        isPrevious
      );
      dispatch(updateSelectedProjectIndex(nextIndex));
    }
  };

  const resolveNextIndex = (
    currentIndex: number,
    itemOrder?: number,
    isPrevious?: boolean
  ): number => {
    let nextIndex: number;

    if (typeof itemOrder === 'number' && isPrevious === undefined) {
      nextIndex = itemOrder;
    } else {
      nextIndex = isPrevious ? currentIndex - 1 : currentIndex + 1;
    }
    return nextIndex;
  };

  const getGlobalIndex = () => {
    if (projectTableFilterState) {
      const {page, pageSize} = projectTableFilterState.paginationState;
      return (page - 1) * pageSize + selectedProjectIndex;
    }
  };

  const fetchProjectByProjectIndex = async (globalIndex: number) => {
    try {
      setShowLoader(true);
      if (projectTableFilterState) {
        const ODataQuery = toODataQuery(
          projectTableFilterState.searchState,
          projectTableFilterState.sortState,
          {page: globalIndex, pageSize: 1}
        );

        const result = await getFilteredProjectList(clientId, ODataQuery);

        const selectedProject = result.data.data.data[0];
        const totalProjectCount = result.data.data.totalCount;

        setNavigationStates(globalIndex, totalProjectCount);
        dispatch(updateSelectedProjectId(selectedProject.projectId));

        getPermissionsByClientIdProjectId(
          selectedProject?.projectId?.toLowerCase()
        );

        const projectDetails = await getProjectByProjectId(
          selectedProject.projectId
        );
        setProjectData(projectDetails.data?.data);
      }

      setShowLoader(false);
    } catch (error) {
      console.error('fetchProjectByProjectId', error);
    }
  };
  const setNavigationStates = async (
    currentProjectOrder: number,
    projectCount: number
  ) => {
    setDepartmentDrawerNavigationState((pv) => {
      return {
        ...pv,
        currentItemOrder: currentProjectOrder,
        totalItemCount: projectCount,
      };
    });
  };

  const updateProject = async (project: ProjectFields) => {
    try {
      await updateProjectByProjectId(project.projectId ?? '', project);
      setProjectData((pv) => {
        if (pv && project) {
          return {...pv, ...(project as Partial<IProjectDetails>)};
        }
      });
      setProjectEditFlag(defaultEditFlag);

      showToast(
        t('clientDetailsPage.projectDrawer.saveSuccessToastMsg'),
        'success',
        undefined,
        'center'
      );

      return true;
    } catch (error) {
      console.error('updateProjecttByProjectId', error);

      showToast(
        t('clientDetailsPage.projectDrawer.saveErrorToastMsg'),
        'error',
        undefined,
        'center'
      );

      return false;
    }
  };

  const updateProjectHandler = async (project: ProjectFields) => {
    return await updateProject({
      ...project,
      projectId: selectedProjectId,
      clientId,
    });
  };
  const updateEditFlag = (editFlag: Partial<EditFlag>) => {
    setProjectEditFlag({...defaultEditFlag, ...editFlag});
  };
  const onEditClickHandler = (editFlag: Partial<EditFlag>) => {
    if (isEditInProgress()) {
      showModal({
        title: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.title'
        ),
        description: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.description'
        ),
        labelOkButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.okButtonLabel'
        ),
        labelCancelButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.cancelButtonLabel'
        ),
        onOk: () => updateEditFlag(editFlag),
      });
    } else {
      updateEditFlag(editFlag);
    }
  };
  const onCancelClickHandler = (flags: Partial<EditFlag>) => {
    updateEditFlag(flags);
  };

  useEffect(() => {
    if (selectedProjectIndex && selectedProjectIndex > 0) {
      setDrawerOpen(true);
      const globalIndex = getGlobalIndex();
      fetchProjectByProjectIndex(globalIndex ?? 0);
    } else {
      setDrawerOpen(false);
    }
  }, [selectedProjectIndex]);

  const handleOnCloseDrawer = () => {
    if (isEditInProgress()) {
      showModal({
        title: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.title'
        ),
        description: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.description'
        ),
        labelOkButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.okButtonLabel'
        ),
        labelCancelButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.cancelButtonLabel'
        ),
        onOk: closeDrawer,
      });
    } else {
      closeDrawer();
    }
  };

  const closeDrawer = () => {
    dispatch(TriggerProjectsAndSummary());
    setDrawerOpen(false);
    setProjectEditFlag(defaultEditFlag);

    dispatch(updateSelectedProjectId(''));
    dispatch(updateSelectedProjectIndex(-1));
  };

  const panelHeader = (
    <Box className={`${styles.departmentsDrawerHeader}`}>
      <Box>
        <AurButton
          label={t('clientDetailsPage.projectDrawer.close')}
          type="exit"
          classNames={'drawer-close-button'}
          size="Medium"
          title={t('clientDetailsPage.projectDrawer.close')}
          ariaLabel={t('clientDetailsPage.projectDrawer.close')}
          onClick={() => handleOnCloseDrawer()}
        />
      </Box>

      <Box className={`${styles.departmentsDrawerHeaderRightComponent}`}>
        <Box>
          <AurAskLioDrawerHeaderButton />
        </Box>
        <Box>
          <AurDrawerNavigation
            currentItemOrder={departmentDrawerNavigationState.currentItemOrder}
            totalItemCount={departmentDrawerNavigationState.totalItemCount}
            navigationClickHandler={departmentDrawerNavigatonHandler}
            previousButtonAriaLabel={t('drawerNavigation.previous')}
            nextButtonAriaLabel={t('drawerNavigation.next')}
          />
        </Box>
      </Box>
    </Box>
  );
  const panelContainer = (
    <AurDrawerPanelContainer
      panelHeader={panelHeader}
      contentHeader={<></>}
      contentBody={
        showLoader ? (
          <AurLoadingIndicator show={showLoader} />
        ) : (
          <Box className={`${styles.projectDrawerPanelBody}`}>
            <ProjectInfoSection ProjectInfo={projectData ?? null} />
            <ECADatabaseSection
              enableEarlyCaseAssessment={projectData?.ecaEnabled ?? false}
              enableNLP={projectData?.nplSearchEnabled ?? false}
            />
            <ProjectSectionHeader
              headerText={t(
                'clientDetailsPage.projectDrawer.moreProjectDetails'
              )}
            />
            <BusinessStructure
              branchId={projectData?.branchId ?? ''}
              branchName={projectData?.branchName ?? ''}
              departmentId={projectData?.departmentId ?? ''}
              departmentName={projectData?.departmentName ?? ''}
              isEdit={projectEditFlag.businessStructure}
              onEditClickHandler={onEditClickHandler}
              onCancelClickHandler={onCancelClickHandler}
              onSaveChangeHandler={updateProjectHandler}
            />
            <ProjectSectionDivider />
            <ProjectDescription
              projectDescription={projectData?.description ?? ''}
              isEdit={projectEditFlag.projectDescription}
              onEditClickHandler={onEditClickHandler}
              onCancelClickHandler={onCancelClickHandler}
              onSaveChangeHandler={updateProjectHandler}
              isEditButtonVisible={hasEditProjectProfilePerm}
            />

            <ProjectSectionDivider />
            <ProjectClientInformation
              entityName={projectData?.entityName ?? ''}
              clientUniqueProjectId={projectData?.clientUniqueProjectId ?? ''}
              internalProjectName={projectData?.internalProjectName ?? ''}
              saveChangesClickHandler={updateProjectHandler}
              editClickHandler={onEditClickHandler}
              cancelClickHandler={onCancelClickHandler}
              isEdit={projectEditFlag.clientInformation}
              hasEditProjectProfilePerm={hasEditProjectProfilePerm}
            />
            <ProjectSectionDivider />
            <ProjectOverseeingAttorneys
              attorneyNames={projectData?.attorneysName ?? ''}
              attorneyEmailAddresses={projectData?.attorneysEmail ?? ''}
              attorneyAdditionalContactInfo={
                projectData?.attorneysAdditionalContactInfo ?? ''
              }
              saveChangesClickHandler={updateProjectHandler}
              editClickHandler={onEditClickHandler}
              cancelClickHandler={onCancelClickHandler}
              isEdit={projectEditFlag.overseeingAttorneys}
              hasEditProjectProfilePerm={hasEditProjectProfilePerm}
            />
            <ProjectSectionDivider />
            <WorkspacesSection workspaces={projectData?.workspaces ?? []} />
          </Box>
        )
      }
    />
  );
  return (
    <AurDrawer
      className={`project-drawer`}
      open={drawerOpen}
      onClose={() => handleOnCloseDrawer()}
      anchor="right"
      drawerContent={panelContainer}
    />
  );
};

export default ProjectDrawer;
